import { FormLayout } from 'components/common'
import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { convertTo12HourFormat } from 'utils/helpers'
import { roundToFourDecimals } from 'utils/helpers/round'

import { FocOrder } from 'types/foc'
import { getStatus } from 'components/orderManagement'

const FocOrderDetailsSection = ({ order }: { order: FocOrder }) => {
  const {
    appliesToMagentoOrderNo,
    createdAt,
    orderErpSync,
    items,
    locationCode,
    totalAmountExclVAT,
    totalAmountInclVAT,
    totalVATAmount,
    orderNo,
    rma,
    storeName,
    orderType,
  } = order

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-4 ">
          <h3 className="title">FOC Order Details</h3>
        </div>
        <div>
          <div className="flex flex-row-reverse items-center gap-2">
            <p className="rounded-lg bg-gray-800 px-3 py-1 text-sm font-semibold text-white shadow">
              {orderNo}
            </p>
          </div>
        </div>
      </div>

      <FormLayout className="!p-4 text-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-bold ">Order Source: </span>
              {orderType === 'MAGENTO' ? 'Magento' : 'Aggregator'}
            </p>

            <p>
              <span className="font-bold">Applies To Magento Order:</span>{' '}
              {appliesToMagentoOrderNo ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Store Code:</span>{' '}
              {locationCode ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Store Name:</span>{' '}
              {storeName ?? 'N/A'}
            </p>
            <p>
              <span className="font-bold">Order Date:</span>{' '}
              {createdAt ? convertTo12HourFormat(createdAt) : 'N/A'}
            </p>
            <p>
              <span className="font-bold">Erp Sync :</span>{' '}
              <span
                className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${
                  orderErpSync
                    ? 'bg-green-400 text-black'
                    : 'bg-red-500  text-white'
                }`}
              >
                {getStatus(orderErpSync)}
              </span>
            </p>

            <p>
              <span className="font-bold">Recon Erp Sync :</span>{' '}
              <span
                className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${
                  order?.orderReconErpSync
                    ? 'bg-green-400 text-black'
                    : 'bg-red-500  text-white'
                }`}
              >
                {getStatus(order?.orderReconErpSync)}
              </span>
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <p>
              <span className="font-bold">Total VAT Amount:</span>{' '}
              {roundToFourDecimals(totalVATAmount) ?? 'NA'}
            </p>
            <p>
              <span className="font-bold">Total amount Excl. VAT:</span>{' '}
              {roundToFourDecimals(totalAmountExclVAT) ?? 'NA'}
            </p>
            <p>
              <span className="font-bold">Total amount Incl. VAT:</span>{' '}
              {roundToFourDecimals(totalAmountInclVAT) ?? 'NA'}
            </p>

            <p>
              <span className="font-bold ">Order Created By:</span>{' '}
              <span className="capitalize">{order?.createdBy ?? 'NA'}</span>
            </p>

            {rma?.isFullRmaCreated && (
              <>
                <p>
                  <span className="font-bold">RMA Erp Sync :</span>{' '}
                  <span
                    className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${
                      rma?.rmaErpSync
                        ? 'bg-green-400 text-black'
                        : 'bg-red-500  text-white'
                    }`}
                  >
                    {getStatus(rma?.rmaErpSync)}
                  </span>
                </p>

                <p>
                  <span className="font-bold">RMA Recon Erp Sync :</span>{' '}
                  <span
                    className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${
                      rma?.rmaReconErpSync
                        ? 'bg-green-400 text-black'
                        : 'bg-red-500  text-white'
                    }`}
                  >
                    {getStatus(rma?.rmaReconErpSync)}
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      </FormLayout>

      <h3 className="title mt-3">Products</h3>
      {items?.length > 0 ? (
        <Table
          headers={[
            'Barcode',
            'Quantity',
            'Unit Price (Incl. Tax)',
            'Unit Price (Excl. Tax)',
            'Unit VAT',
          ]}
        >
          {items.map((item) => (
            <TableRow key={item?.barcodeNo}>
              {/* Product Details */}
              <TableCell>
                <p className="mb-1 font-bold">{item?.itemDesc ?? '-'}</p>
                <p>
                  <strong>SKU:</strong> {item?.barcodeNo}
                </p>
                <p>
                  <strong>UOM:</strong> {item?.itemUom || 'NA'}
                </p>
              </TableCell>
              {/* Quantity Column */}
              <TableCell>
                <p>{item?.quantity ?? '-'}</p>
              </TableCell>
              {/* Price Including Tax Column  */}
              <TableCell>
                {roundToFourDecimals(item?.lineAmountInclVAT) ?? 'NA'}
              </TableCell>
              <TableCell>
                {roundToFourDecimals(item?.lineAmountExclVAT) ?? 'NA'}
              </TableCell>
              <TableCell>
                {roundToFourDecimals(item?.vatAmount) ?? 'NA'}
              </TableCell>
            </TableRow>
          ))}
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No Item details available</p>
      )}

      <h3 className="title mt-3">RMA History</h3>
      {rma?.isFullRmaCreated ? (
        <Table headers={['RMA Number', 'Type', 'RMA Created By']}>
          <TableRow key={rma.rmaNo}>
            <TableCell>{rma?.rmaNo ?? 'NA'}</TableCell>
            <TableCell>Full RMA</TableCell>
            <TableCell>{rma?.createdBy ?? '-'}</TableCell>
          </TableRow>
        </Table>
      ) : (
        <p className="text-sm text-gray-600">No RMAs available</p>
      )}
    </>
  )
}

export default FocOrderDetailsSection
